import _ from 'underscore';
import ko from 'knockout';

/**
 * Humanize number by number of digits and i18n
 * @param {number} num - number to be humanized
 * @param {int} numberOfDigits - number of digits
 * @param {I18n} i18n - provide a property decimal.fromFloatToString,
 * the function is different for differnent lang
 * @returns {string} - return a number string
 */
function humanizeNumber(num, numberOfDigits, i18n) {
  if (!_.isNumber(num)) {
    return '';
  }

  const str = String(num);
  const decimalPointIndex = str.indexOf('.');
  const numberOfDigitsOfValue = decimalPointIndex === -1 ? 0 : str.length - decimalPointIndex - 1;

  return i18n.formatDecimalToFixed(
    num,
    { digits: Math.max(numberOfDigits, numberOfDigitsOfValue) }
  );
}

/**
 * Extend number, add number convert between string and number
 * @param {int} numberOfDigits - number of digits
 * @param {Observable} local - observable to be extend
 * @param {Object} prop - property define object
 * @param {I18n} i18n - provide a property decimal.fromFloatToString to convert number
 * @returns {undefined} - extend local in function
 */
function numberExtension(numberOfDigits, local, prop, i18n) {
  // cache user input, never correct user
  _.extend(local, {
    /**
     * internal use property
     * @private
     */
    str: ko.observable(humanizeNumber(local(), _.result(prop, 'numberOfDigits', numberOfDigits), i18n)),
    locked: false,
  });

  _.extend(local, {
    /**
     * external use property
     * @public
     */
    string: ko.computed({
      read: () => local.str(),
      write: (value) => {
        _.extend(local, { locked: true });

        if (value === '') {
          local(null);
        } else if (i18n.isValidDecimal(value)) {
          const num = i18n.parseDecimal(value);

          local(num);

          // elsewhere though str is not updated, it will not notify subscribers
          if (local() === num) { // input is not blocked by validator
            local.str(value);
          } else {
            local.str.notifySubscribers();
          }
        } else {
          // no block user input when type is wrong
          // block input will cause no chance to edit a correct number
          // For example: 1.20 -> remove 1 -> ".20" is not a valid number, blocked
          local(value);
        }

        _.extend(local, { locked: false });
      },
    }).extend({ notify: 'always' }),
  });

  local.subscribe(() => {
    if (!local.locked) {
      local.str(humanizeNumber(local(), numberOfDigits, i18n));
    }
  });
}

/**
 * Extend enum, add function for enum observable
 * @param {Observable} local - enum observable to extend
 * @param {Object} prop - enum property define
 * @returns {undefined} - extend local
 */
function enumExtension(local, prop) {
  const writeable = _.size(prop.enum) === _.size([true, false]);

  _.each(prop.enum, (item, index) => {
    const param = {
      read: function read() {
        return local() === item;
      },
      write: function write(value) {
        local(value ? item : prop.enum[1 - index]);
      },
    };

    _.extend(local, _.object([`is${item}`], [ko.computed(writeable ? param : param.read)]));
  });
}

const typeSpecified = {
  integer: _.partial(numberExtension, 0),
  number: _.partial(numberExtension, 2),
  boolean: (local, prop) => {
    _.extend(local, {
      string: ko.computed({
        read: () => {
          const value = local();

          if (prop.nullable && _.isNull(value)) {
            return 'null';
          }
          return value.toString();
        },
        write: (value) => {
          if (prop.nullable && value === 'null') {
            local(null);
          } else {
            local(value === 'true');
          }
        },
      }),
    });
  },
  string: _.noop, // moved to ko schema string
  object: _.noop,
  array: _.noop,
  datetime: _.noop,
};

/**
 * extension property base on property type, special for integrate, number,
 * boolean and enum, convert between UI input and background
 * @param {Object} local - Object to extension
 * @param {Object} prop - property define
 * @param {I18n} i18n - provide property decimal to convert number
 * @returns {undefined} - extend property in local
 */
export function Extension(local, prop, i18n) {
  _.extend(local, { editing: ko.observable() });

  if (prop.enum) {
    enumExtension(local, prop);
  } else {
    typeSpecified[prop.type](local, prop, i18n);
  }

  return local;
}
